// react
import { useContext, useEffect, useState } from 'react';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// types
import {
	Account,
	CashInMethod,
	Category,
	Component,
	Entity,
	FinancierExt,
	Loan,
	Transaction,
} from '../../../@types';
import { financiersExt } from '../../../@types/unions';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';
import { useReimbursements } from '../../../hooks/useReimbursements';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, BoxProps, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';
import {
	TableBasicTransactions,
	TableBasicTransactionsProps,
} from '../../tables/basic/TableBasicTransactions/TableBasicTransactions';

// ui
import { BoxKeyValue } from '../../ui/BoxKeyValue/BoxKeyValue';
import { entityIconTypes, IconCircleProps } from '../../ui/IconCircle/IconCircle';
import {
	TableInfoTransaction,
	TableInfoTransactionLineItem,
} from '../../ui/TableInfoTransaction/TableInfoTransaction';

// tabs
import { TabsTransaction } from '../../tabs/TabsTransaction/TabsTransaction';

// utils
import { formatCurrency } from '../../../utils/currency';

// theme
import { theme } from '../../../theme';

// props
export interface DialogTransactionsProps extends DialogActionsProps {
	currency?: string;
	fromEntity?: Entity;
	isOpen: boolean;
	onClose: () => void;
	TableBasicTransactionsProps?: Partial<TableBasicTransactionsProps>;
	toEntity?: Entity;
	transactions?: Transaction[];
}

export const DialogTransactions = ({
	currency,
	fromEntity,
	isOpen,
	onClose,
	TableBasicTransactionsProps,
	toEntity,
	transactions: transactionsProp = [],
	...props
}: DialogTransactionsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [account, setAccount] = useState<Account | null>(null);
	const [cashInMethod, setCashInMethod] = useState<CashInMethod | null>(null);
	const [category, setCategory] = useState<Category | null>(null);
	const [component, setComponent] = useState<Component | null>(null);
	const [loan, setLoan] = useState<Loan | null>(null);
	const [transactions, setTransactions] = useState<Transaction[]>([]);

	// derived
	let cashOutForLoan;

	if (loan) {
		if (category) {
			cashOutForLoan = category.cashOut.find((c) => c.loan === loan.id);
		} else if (component) {
			cashOutForLoan = component.cashOut.find((c) => c.loan === loan.id);
		}
	}

	const reimbursementIds = cashOutForLoan?.reimbursements;

	// hooks
	const { isLoadingReimbursements, reimbursements } = useReimbursements({
		fieldsReimbursements: 'amount,currency,id,date,payments,transactions,type',
		fieldsTransactionsApplied: 'date,id,payments,type',
		reimbursementIds,
	});
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);

	// params
	const paramDialogAccount = searchParams.get('dialogAccount');
	const paramDialogCashInMethod = searchParams.get('dialogCashInMethod');
	const paramDialogCashOutAccumulatedDateEnd = searchParams.get('dialogCashOutAccumulatedDateEnd');
	const paramDialogCashOutDateEnd = searchParams.get('dialogCashOutDateEnd');
	const paramDialogCashOutDateStart = searchParams.get('dialogCashOutDateStart');
	const paramDialogCategory = searchParams.get('dialogCategory');
	const paramDialogComponent = searchParams.get('dialogComponent');
	const paramDialogLoan = searchParams.get('dialogLoan');
	const paramDialogTransactions = searchParams.get('dialogTransactions');

	// fetch transactions if param exists
	const { fetchRequest: fetchTransactions, isLoading: isLoadingTransactions } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/transactions?id=${paramDialogTransactions}&populate=activities,activities.actor,expenseType,payments,payments.conversions,payments.reimbursed.conversions`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactions(res.data);
			}
		},
	});

	// fetch account if param exists
	const { fetchRequest: fetchAccount } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/accounts/${paramDialogAccount}?fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setAccount(res.data[0]);
			}
		},
	});

	// fetch cashInMethod if param exists
	const { fetchRequest: fetchCashInMethod } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/cash-in-methods/${paramDialogCashInMethod}?fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCashInMethod(res.data[0]);
			}
		},
	});

	// fetch category if param exists
	let urlFetchCategories = `${process.env.REACT_APP_API_URL}/categories/${paramDialogCategory}?project=${project?.id}&fields=cashOut,id,name`;

	if (paramDialogCashOutAccumulatedDateEnd) {
		urlFetchCategories += `&cashOutAccumulatedDateEnd=${paramDialogCashOutAccumulatedDateEnd}`;
	}

	if (paramDialogCashOutDateEnd) {
		urlFetchCategories += `&cashOutDateEnd=${paramDialogCashOutDateEnd}`;
	}

	if (paramDialogCashOutDateStart) {
		urlFetchCategories += `&cashOutDateStart=${paramDialogCashOutDateStart}`;
	}

	const { fetchRequest: fetchCategory } = useFetch({
		isLazy: true,
		url: urlFetchCategories,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCategory(res.data[0]);
			}
		},
	});

	// fetch component if param exists
	let urlFetchComponents = `${process.env.REACT_APP_API_URL}/components/${paramDialogComponent}?project=${project?.id}&fields=cashOut,id,name`;

	if (paramDialogCashOutAccumulatedDateEnd) {
		urlFetchComponents += `&cashOutAccumulatedDateEnd=${paramDialogCashOutAccumulatedDateEnd}`;
	}

	if (paramDialogCashOutDateEnd) {
		urlFetchComponents += `&cashOutDateEnd=${paramDialogCashOutDateEnd}`;
	}

	if (paramDialogCashOutDateStart) {
		urlFetchComponents += `&cashOutDateStart=${paramDialogCashOutDateStart}`;
	}

	const { fetchRequest: fetchComponent } = useFetch({
		isLazy: true,
		url: urlFetchComponents,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setComponent(res.data[0]);
			}
		},
	});

	// fetch loan if param exists
	const { fetchRequest: fetchLoan } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/loans/${paramDialogLoan}?fields=code,financier,id`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoan(res.data[0]);
			}
		},
	});

	// vars
	const headersIncluded = ['amount', 'date', 'tax', 'total', 'type'];

	// add reimbursed header for external financiers
	if (paramDialogCategory || paramDialogComponent) {
		if (financiersExt.includes(loan?.financier as FinancierExt)) {
			headersIncluded.push('reimbursed');
		}
	}

	let total = 0;
	let totalApplied: number | undefined;
	let totalReimbursed: number | undefined;
	let paymentsForTotal: Transaction['payments'] = [];

	let currencyForTotal = currency;
	if (!currencyForTotal && transactions.length) {
		currencyForTotal = transactions[0].currency;
	}

	if (cashOutForLoan) {
		totalApplied = cashOutForLoan.totalApplied;
		totalReimbursed = cashOutForLoan.totalReimbursed;
	}

	if (transactions.length && !isLoadingTransactions) {
		transactions
			.filter((t) => t.status !== 'REVERTED')
			.forEach((t) => {
				if (t.payments?.length) {
					paymentsForTotal = [...paymentsForTotal, ...t.payments];
				}
			});

		// only include DONE (or STARTED for local) payments and filter out reverted payments
		const blkStatusTarget = process.env['REACT_APP_ENV'] === 'local' ? 'STARTED' : 'DONE';
		paymentsForTotal = paymentsForTotal.filter(
			(p) => p.blkTransaction.status === blkStatusTarget && !p.revertedPayment
		);

		if (paramDialogLoan) {
			// filter payments for loan if param exists
			paymentsForTotal = paymentsForTotal.filter((p) => p.loan === Number(paramDialogLoan));
		}

		// calculate total using payment or conversion
		for (let i = 0; i < paymentsForTotal.length; i++) {
			const p = paymentsForTotal[i];
			let amountInCurrency = (p.amount || 0) + (p.tax || 0);

			// get converted amount if currency doesn't match
			if (currencyForTotal && p.currency !== currencyForTotal) {
				if (p.conversions) {
					const conversion = p.conversions.find((c) => c.currency === currencyForTotal);
					if (conversion) {
						amountInCurrency = conversion.amount + (conversion.tax || 0);
					} else {
						// Skip this payment if no conversion is available
						continue;
					}
				} else {
					// Skip this payment if no conversions array and currency doesn't match
					continue;
				}
			}
			total += amountInCurrency;
		}
	}

	const getIconCircleProps = (entity: Entity) => {
		const iconCircleProps: IconCircleProps = {
			type: 'default',
		};

		if (entity) {
			if (entityIconTypes[entity.type]) {
				iconCircleProps.type = entityIconTypes[entity.type];
			} else {
				const entityName = entity.name.toLowerCase();

				iconCircleProps.color = 'grey';

				if (entityName.includes('other')) {
					iconCircleProps.type = 'other';
				} else if (entityName.includes('government')) {
					iconCircleProps.type = 'government';
				} else if (entityName.includes('world bank')) {
					iconCircleProps.type = 'account';
				}
			}
		}

		return iconCircleProps;
	};

	let fromEntityIconCircleProps: IconCircleProps = {};
	let toEntityIconCircleProps: IconCircleProps = {};

	if (fromEntity) {
		fromEntityIconCircleProps = getIconCircleProps(fromEntity);
	}

	if (toEntity) {
		toEntityIconCircleProps = getIconCircleProps(toEntity);
	}

	const transactionsEnhanced = transactions.map((transaction, index) => {
		const isLast = index === transactions.length - 1;
		const BoxProps: BoxProps = {};
		const tabs = ['documents', 'comments', 'tags'];

		if (!isLast) {
			BoxProps.sx = {
				borderBottomColor: theme.palette.divider,
				borderBottomStyle: 'solid',
				borderBottomWidth: 1,
			};
		}

		if (transaction.type === 'REIMBURSEMENT') {
			tabs.push('applied');
		}

		return {
			...transaction,
			expandedContent: (
				<TabsTransaction BoxProps={BoxProps} tabs={tabs} transaction={transaction} />
			),
		};
	});

	const reimbursementsEnhanced = reimbursements.map((transaction, index) => {
		const isLast = index === reimbursements.length - 1;
		const BoxProps: BoxProps = {};

		if (!isLast) {
			BoxProps.sx = {
				borderBottomColor: theme.palette.divider,
				borderBottomStyle: 'solid',
				borderBottomWidth: 1,
			};
		}

		return {
			...transaction,
			expandedContent: (
				<TabsTransaction
					BoxProps={BoxProps}
					TableProps={{
						frame: 'DIALOG',
					}}
					tabs={['applied', 'documents', 'comments', 'tags']}
					transaction={transaction}
				/>
			),
		};
	});

	const hasTableTransaction = account || category || cashInMethod || component || loan;
	const lineItems: TableInfoTransactionLineItem[] = [];

	let subtitle: DialogActionsProps['subtitle'];

	if (total) {
		// add paid line if totalApplied or totalReimbursed are greater than 0
		if ((totalApplied && totalApplied !== 0) || (totalReimbursed && totalReimbursed !== 0)) {
			lineItems.push({
				IconCircleProps: {
					type: 'payment',
				},
				label: {
					children: t('paid'),
				},
				value: {
					children: formatCurrency({
						currency: currencyForTotal,
						value: total,
					}),
				},
			});
		}
	}

	// add applied line item
	if (totalApplied) {
		lineItems.push({
			IconCircleProps: {
				type: 'reimbursement',
			},
			label: {
				children: t('applied'),
			},
			value: {
				children: (
					<Typography color="success.main">
						+{' '}
						{formatCurrency({
							currency: currencyForTotal,
							value: totalApplied,
						})}
					</Typography>
				),
			},
		});
	}

	// add reimbursed line item
	if (totalReimbursed) {
		lineItems.push({
			IconCircleProps: {
				type: 'reimbursement',
			},
			label: {
				children: t('reimbursed'),
			},
			value: {
				children: (
					<Typography color="error.main">
						-{' '}
						{formatCurrency({
							currency: currencyForTotal,
							value: totalReimbursed,
						})}
					</Typography>
				),
			},
		});
	}

	// add total line item
	if (total) {
		let totalToDisplay = total;

		if (totalApplied) {
			totalToDisplay += totalApplied;
		}

		if (totalReimbursed) {
			totalToDisplay -= totalReimbursed;
		}

		const totalFormatted = `${formatCurrency({
			currency: currencyForTotal,
			value: totalToDisplay,
		})} ${t('total')}`;

		subtitle = {
			children: totalFormatted,
			variant: 'body2',
		};

		lineItems.push({
			IconCircleProps: {
				type: 'payment',
			},
			label: {
				children: t('total'),
			},
			value: {
				children: (
					<Typography fontWeight={600}>
						{formatCurrency({
							currency: currencyForTotal,
							value: totalToDisplay,
						})}
					</Typography>
				),
			},
		});
	}

	// update transactions when transactions prop changes
	useEffect(() => {
		if (transactionsProp.length) {
			setTransactions(transactionsProp);
		}
	}, [transactionsProp]);

	// fetch or set account when params change
	useEffect(() => {
		if (paramDialogAccount) {
			fetchAccount();
		} else {
			setAccount(null);
		}
	}, [fetchAccount, paramDialogAccount]);

	// fetch or set cashInMethod when params change
	useEffect(() => {
		if (paramDialogCashInMethod) {
			fetchCashInMethod();
		} else {
			setCashInMethod(null);
		}
	}, [fetchCashInMethod, paramDialogCashInMethod]);

	// fetch or set category when params change
	useEffect(() => {
		if (paramDialogCategory) {
			fetchCategory();
		} else {
			setCategory(null);
		}
	}, [fetchCategory, paramDialogCategory]);

	// fetch or set component when params change
	useEffect(() => {
		if (paramDialogComponent) {
			fetchComponent();
		} else {
			setComponent(null);
		}
	}, [fetchComponent, paramDialogComponent]);

	// fetch or set loan when params change
	useEffect(() => {
		if (paramDialogLoan) {
			fetchLoan();
		} else {
			setLoan(null);
		}
	}, [fetchLoan, paramDialogLoan]);

	// fetch or set transactions when params change
	useEffect(() => {
		if (paramDialogTransactions) {
			fetchTransactions();
		} else {
			setTransactions([]);
		}
	}, [fetchTransactions, paramDialogTransactions]);

	// clear state on close
	useEffect(() => {
		return () => {
			setLoan(null);
			setTransactions([]);
		};
	}, []);

	return (
		<DialogActions
			maxWidth={1000}
			{...props}
			hasClose={true}
			isOpen={isOpen}
			onClose={onClose}
			subtitle={subtitle}
			title={{
				children: t('transactions'),
				variant: 'h3',
			}}>
			<Grid container spacing={2}>
				{fromEntity && (
					<Grid item xs={12} md={6}>
						<BoxKeyValue
							IconCircleProps={fromEntityIconCircleProps}
							label={{
								children: t('sent'),
							}}
							value={{
								children: fromEntity?.name,
							}}
						/>
					</Grid>
				)}
				{toEntity && (
					<Grid item xs={12} md={6}>
						<BoxKeyValue
							IconCircleProps={toEntityIconCircleProps}
							label={{
								children: t('received'),
							}}
							value={{
								children: toEntity?.name,
							}}
						/>
					</Grid>
				)}
				{hasTableTransaction && (
					<Grid item xs={12}>
						<TableInfoTransaction
							account={account}
							cashInMethod={cashInMethod}
							category={category}
							component={component}
							lineItems={lineItems}
							loan={loan}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<TableBasicTransactions
						borderRadius={2}
						color="grey"
						hasBorder={true}
						headersIncluded={headersIncluded}
						// Presentational props can be overriden
						{...TableBasicTransactionsProps}
						data={transactionsEnhanced}
						isLoading={isLoadingTransactions}
						loan={paramDialogLoan ? Number(paramDialogLoan) : undefined}
					/>
				</Grid>
				{reimbursementsEnhanced.length > 0 && (
					<Grid item xs={12}>
						<TableBasicTransactions
							borderRadius={2}
							color="grey"
							hasBorder={true}
							headersIncluded={['applied', 'date', 'total', 'type']}
							// Presentational props can be overriden
							{...TableBasicTransactionsProps}
							data={reimbursementsEnhanced}
							isLoading={isLoadingReimbursements}
							loan={paramDialogLoan ? Number(paramDialogLoan) : undefined}
						/>
					</Grid>
				)}
				{loan && (
					<Grid item xs={12}>
						<Alert severity="info">
							<Trans
								components={{
									strong: <Typography component="strong" fontWeight={600} />,
								}}
								i18nKey="alert.transactionsForLoanOnly"
								values={{ loanCode: loan?.code }}
							/>
						</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
